// Who can use this thing? — `roles.json`, plus the Postgres grant overlay.
//
// The asymmetry is the whole panel and it is stated where somebody is about to act on it:
//
//   * A **grant** can live in Postgres. Unreadable, it degrades closed — the new person cannot
//     sign in until Postgres is back.
//   * A **revocation of a role roles.json grants** cannot. Unreadable, it would degrade open.
//     So Revoke refuses that half and hands back the exact file diff to paste into a PR.
//
// Revoke is not a no-op, though. A grant this overlay made *is* taken back here, because
// withdrawing it is fail-closed in the same way granting it was. The reply says which half
// happened, and the button never claims a removal that did not.

import { useState } from 'react';
import { grantRole, panelMessage, revokeRole } from '../api/client';
import type { AdminRolesPayload, RolePerson } from '../api/types';
import { useAuthContext } from '../auth/AuthContext';
import { StatePill } from './AdminSection';
import { formatStamp } from './format.ts';

function sourceLabel(person: RolePerson): string {
  if (person.file_role && person.override_role) return 'file + grant';
  if (person.file_role) return 'roles.json';
  if (person.override_role) return 'Postgres grant';
  return 'none';
}

function PersonRow({
  person,
  busy,
  onRevoke,
}: {
  person: RolePerson;
  busy: boolean;
  onRevoke: (email: string) => void;
}) {
  return (
    <tr className="acct">
      <td><span className="acctname">{person.email}</span></td>
      <td>
        {person.effective_role
          ? <span className="chip sig">{person.effective_role}</span>
          : <span className="dash">refused</span>}
      </td>
      <td>
        {sourceLabel(person)}
        {person.set_by ? <div className="sm">granted by {person.set_by}</div> : null}
        {person.set_at ? <div className="sm">{formatStamp(person.set_at)}</div> : null}
        {person.note ? <div className="sm">{person.note}</div> : null}
      </td>
      <td>
        {person.needs_deploy_to_remove
          ? <StatePill tone="warn" label="needs a deploy" />
          : <StatePill tone="ok" label="removable here" />}
      </td>
      <td>
        <button
          type="button"
          className="btn"
          disabled={busy}
          onClick={() => onRevoke(person.email)}
        >
          Revoke
        </button>
      </td>
    </tr>
  );
}

export function RolesEditor({
  payload,
  onChanged,
}: {
  payload: AdminRolesPayload | undefined;
  onChanged: (next: AdminRolesPayload) => void;
}) {
  const { getToken } = useAuthContext();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('strategist');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState<AdminRolesPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  const people = payload?.people ?? [];
  const grantable = payload?.grantable_roles ?? ['admin', 'leadership', 'strategist', 'viewer'];

  async function submitGrant() {
    if (!email.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const next = await grantRole(getToken, { email: email.trim(), role, note: note.trim() });
      setReply(next);
      onChanged(next);
      if (next.applied) {
        setEmail('');
        setNote('');
      }
    } catch (caught) {
      setError(panelMessage(caught, 'The grant could not be written.'));
    } finally {
      setBusy(false);
    }
  }

  async function submitRevoke(target: string) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const next = await revokeRole(getToken, { email: target });
      setReply(next);
      onChanged(next);
    } catch (caught) {
      setError(panelMessage(caught, 'The revocation could not be processed.'));
    } finally {
      setBusy(false);
    }
  }

  async function copyDiff(diff: string) {
    try {
      await navigator.clipboard.writeText(diff);
    } catch {
      // Clipboard can be denied. The diff is on the page either way.
    }
  }

  return (
    <>
      <p className="adminnote">{payload?.revocation_note}</p>

      <div className="grantform">
        <label className="field">
          <span className="fieldlab">Address</span>
          <input
            type="search"
            placeholder="new.strategist@cartesia.ai"
            value={email}
            disabled={busy}
            onChange={event => setEmail(event.target.value)}
          />
        </label>
        <label className="field">
          <span className="fieldlab">Role</span>
          <select value={role} disabled={busy} onChange={event => setRole(event.target.value)}>
            {grantable.map(value => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>
        <label className="field">
          <span className="fieldlab">Note</span>
          <input
            type="search"
            placeholder="why, and who asked"
            value={note}
            disabled={busy}
            onChange={event => setNote(event.target.value)}
          />
        </label>
        <button
          type="button"
          className="btn primary"
          disabled={busy || !email.trim()}
          onClick={() => { void submitGrant(); }}
        >
          {busy ? 'Working…' : 'Grant'}
        </button>
      </div>

      {reply?.reason ? (
        <p className={'actionnote ' + (reply.applied ? 'ok' : 'warn')}>{reply.reason}</p>
      ) : null}
      {error ? <p className="actionnote bad">{error}</p> : null}

      {/* The diff, printed rather than applied. This is the honest half of "remove somebody":
          the file still grants the role until this lands in a PR and deploys. */}
      {reply?.roles_json_diff ? (
        <div className="diffblock">
          <div className="briefbar">
            <span className="briefwhen">access/roles.json — paste into a PR</span>
            <button
              type="button"
              className="btn"
              onClick={() => { void copyDiff(reply.roles_json_diff || ''); }}
            >
              Copy the diff
            </button>
          </div>
          <pre className="diff">{reply.roles_json_diff}</pre>
        </div>
      ) : null}

      {people.length === 0 ? (
        <p className="briefempty">Nobody is listed, which cannot be true — check roles.json.</p>
      ) : (
        <div className="tablewrap">
          <table className="booktable">
            <thead>
              <tr>
                <th>Address</th>
                <th>Resolves to</th>
                <th>From</th>
                <th>Removal</th>
                <th> </th>
              </tr>
            </thead>
            <tbody>
              {people.map(person => (
                <PersonRow
                  key={person.email}
                  person={person}
                  busy={busy}
                  onRevoke={target => { void submitRevoke(target); }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
